import { CreditCard, Wallet, Smartphone, ShieldCheck, MessageCircle, ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { buildWhatsAppLink } from "@/lib/contact";

const METHODS = [
  { id: "gcash",   name: "GCash",         tag: "PH",   icon: "wallet",    accent: "#00b9f1" },
  { id: "maya",    name: "Maya",          tag: "PH",   icon: "smartphone", accent: "#34d399" },
  { id: "grabpay", name: "GrabPay",       tag: "SEA",  icon: "wallet",    accent: "#22d3ee" },
  { id: "card",    name: "Visa / Mastercard", tag: "Intl.", icon: "card", accent: "#a78bfa" },
  { id: "wa",      name: "WhatsApp Pay",  tag: "Direct", icon: "chat",   accent: "#00cfff" },
];

function Glyph({ kind, color }) {
  const cls = "w-5 h-5";
  if (kind === "wallet")     return <Wallet className={cls} style={{ color }} />;
  if (kind === "smartphone") return <Smartphone className={cls} style={{ color }} />;
  if (kind === "card")       return <CreditCard className={cls} style={{ color }} />;
  if (kind === "chat")       return <MessageCircle className={cls} style={{ color }} />;
  return <CreditCard className={cls} style={{ color }} />;
}

export default function PaymentSection({ onActivate }) {
  const { t } = useI18n();
  return (
    <section data-testid="payment-section" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="dncm-pulse-dot" />
            <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
              {t("payment_title")}
            </span>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            {t("payment_subtitle")}
          </h3>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-white/45 font-mono-d uppercase tracking-[0.22em]">
          <ShieldCheck className="w-4 h-4 text-[color:var(--dncm-cyan)]" />
          Secure
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {METHODS.map((m) => (
          <div key={m.id} data-testid={`payment-${m.id}`} className="dncm-card rounded-xl p-5 flex flex-col items-start gap-3">
            <span
              className="flex items-center justify-center w-10 h-10 rounded-lg"
              style={{
                background: `${m.accent}1A`,
                border: `1px solid ${m.accent}55`,
                boxShadow: `0 0 18px ${m.accent}33`,
              }}
            >
              <Glyph kind={m.icon} color={m.accent} />
            </span>
            <div>
              <div className="font-display text-base text-white">{m.name}</div>
              <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5">{m.tag}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-center">
        <button
          data-testid="payment-pay-now"
          onClick={onActivate}
          className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full font-mono-d text-[12px] uppercase tracking-[0.24em] text-[#001018] dncm-btn-glow"
          style={{ background: "linear-gradient(180deg, #6ff3ff 0%, #00cfff 60%, #00a6cf 100%)" }}
        >
          {t("pay_now")}
          <ArrowRight className="w-4 h-4" />
        </button>
        <a
          data-testid="payment-whatsapp"
          href={buildWhatsAppLink("Bonjour, je souhaite activer mon système DNCM Vision et payer via WhatsApp.")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full font-mono-d text-[12px] uppercase tracking-[0.24em] text-white border"
          style={{ borderColor: "rgba(0,207,255,0.5)", background: "rgba(0,207,255,0.04)" }}
        >
          <MessageCircle className="w-4 h-4" />
          {t("activate_system")}
        </a>
      </div>
    </section>
  );
}
